/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CustomTaskFormPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileCode, Search, Plus } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { fetchCustomTask, createCustomTask, updateCustomTask, deleteCustomTask, fetchCronProfiles, fetchScripts, CronProfile, Script, CreateCustomTaskInput, CustomTask } from './api';
import { useCustomTaskQuickList } from './hooks/useCustomTaskQuickList';
import { CustomTaskFormBox } from './components/CustomTaskFormBox';
import { CustomTaskQuickListModal } from './components/CustomTaskQuickListModal';

interface FormData {
    name: string; description: string; scriptPath: string; scriptArgs: string;
    cronProfileId: number | null; timeoutSeconds: number; runAsUser: string; enabled: boolean;
}

const DEFAULT_FORM: FormData = { name: '', description: '', scriptPath: '', scriptArgs: '', cronProfileId: null, timeoutSeconds: 300, runAsUser: 'root', enabled: true };

export default function CustomTaskFormPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const mode = id && id !== 'new' ? 'edit' : 'create';
    const isEditMode = mode === 'edit';

    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [cronProfiles, setCronProfiles] = useState<CronProfile[]>([]);
    const [scripts, setScripts] = useState<Script[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showQuickList, setShowQuickList] = useState(false);
    const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
    const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const hasChanges = originalFormData ? JSON.stringify(formData) !== JSON.stringify(originalFormData) : true;

    // -- Data load ---------------------------------------------
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [profiles, scriptList] = await Promise.all([fetchCronProfiles(), fetchScripts()]);
                setCronProfiles(profiles);
                setScripts(scriptList);
                if (isEditMode && id) {
                    const task = await fetchCustomTask(id);
                    const loaded: FormData = { name: task.name, description: task.description || '', scriptPath: task.scriptPath, scriptArgs: task.scriptArgs || '', cronProfileId: task.cronProfileId, timeoutSeconds: task.timeoutSeconds, runAsUser: task.runAsUser, enabled: task.enabled };
                    setFormData(loaded);
                    setOriginalFormData(loaded);
                } else {
                    setOriginalFormData(DEFAULT_FORM);
                }
            } catch (err: any) { showError(err?.message || t('customTasks.errors.fetchFailed')); }
            finally { setLoading(false); }
        };
        load();
    }, [id, isEditMode]);

    // -- Validation --------------------------------------------
    const validateForm = useCallback(() => {
        const errs: Record<string, string> = {};
        if (!formData.name.trim()) errs.name = t('customTasks.validation.nameRequired');
        if (!formData.scriptPath.trim()) errs.scriptPath = t('customTasks.validation.scriptPathRequired');
        setErrors(errs);
        return { isValid: Object.keys(errs).length === 0, errors: errs };
    }, [formData, t]);

    // -- Save --------------------------------------------------
    const handleSaveInternal = useCallback(async (): Promise<CustomTask | undefined> => {
        if (!validateForm().isValid) { showError(t('customTasks.errors.validationFailed')); return undefined; }
        setIsSaving(true);
        try {
            const payload: CreateCustomTaskInput = { name: formData.name, description: formData.description || undefined, scriptPath: formData.scriptPath, scriptArgs: formData.scriptArgs || undefined, cronProfileId: formData.cronProfileId || undefined, timeoutSeconds: formData.timeoutSeconds, runAsUser: formData.runAsUser, enabled: formData.enabled };
            const saved = isEditMode && id ? await updateCustomTask(id, payload) : await createCustomTask(payload);
            showSuccess(t(isEditMode ? 'customTasks.messages.updateSuccess' : 'customTasks.messages.createSuccess'));
            setOriginalFormData(formData);
            return saved;
        } catch (err: any) { showError(err?.message || t('customTasks.errors.saveFailed')); return undefined; }
        finally { setIsSaving(false); }
    }, [formData, isEditMode, id, validateForm, showSuccess, showError, t]);

    const handleSaveWithAction = useSaveWithAction({ onSave: handleSaveInternal, listPath: '/admin/maintenance/tasks', newPath: '/admin/maintenance/tasks/new', mode, getEditPath: (s) => `/admin/maintenance/tasks/${s.uuid ?? s.id}`, getFormData: () => formData, storageKey: 'customTasks:saveAndNewData' });

    // -- Delete ------------------------------------------------
    const handleDeleteConfirm = async () => {
        if (!isEditMode || !id) return;
        setIsDeleting(true);
        try { await deleteCustomTask(id); showSuccess(t('customTasks.messages.deleteSuccess')); navigate('/admin/maintenance/tasks'); }
        catch (err: any) { showError(err?.message || t('customTasks.errors.deleteFailed')); setShowDeleteModal(false); }
        finally { setIsDeleting(false); }
    };

    // -- Quick list --------------------------------------------
    const { tasks: quickListTasks, search: quickListSearch, setSearch: setQuickListSearch, isLoadingInitial, isSearching } = useCustomTaskQuickList({ isOpen: showQuickList });
    const closeQuickList = () => setShowQuickList(false);

    const setField = (field: keyof FormData, value: unknown) => setFormData((prev) => ({ ...prev, [field]: value }));

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<FileCode className="h-5 w-5 text-primary" />}
                title={isEditMode ? t('customTasks.form.editTitle') : t('customTasks.form.createTitle')}
                backButtonText={t('customTasks.actions.list')}
                onBack={() => navigate('/admin/maintenance/tasks')}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('customTasks.actions.quickList'), onClick: () => setShowQuickList(true) },
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('customTasks.actions.add'), onClick: () => navigate('/admin/maintenance/tasks/new') },
                ]}
            />

            <div className="mt-8">
                <CustomTaskFormBox formData={formData} errors={errors} cronProfiles={cronProfiles} scripts={scripts} onChange={setField} />
            </div>

            <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={() => { if (originalFormData) { setFormData({ ...originalFormData }); setErrors({}); } }} onDelete={() => { if (isEditMode && id) setShowDeleteModal(true); }} isEditMode={isEditMode} isSaving={isSaving} hasChanges={hasChanges} cancelText={t('customTasks.actions.cancel')} deleteText={t('customTasks.actions.delete')} useSaveDropdown saveDropdownStorageKey="customTasks:saveAction" saveDisabled={isSaving} />

            <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDeleteConfirm} title={t('customTasks.dialogs.delete.title')} message={t('customTasks.dialogs.delete.description')} confirmText={t('customTasks.actions.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting} />

            <CustomTaskQuickListModal isOpen={showQuickList} currentItemId={id} tasks={quickListTasks} isLoadingInitial={isLoadingInitial} isSearching={isSearching} searchValue={quickListSearch} onSearchChange={setQuickListSearch} onClose={closeQuickList} />
        </div>
    );
}
